/**
 * UserSearchCombobox — pick a user by name or email, not by UUID.
 *
 * Nobody knows anyone's UUID. Any admin screen that asks for one is really asking the operator
 * to go and run a query first, which is how you end up comping the wrong account.
 *
 * Matches the established pattern in this repo rather than adding a dependency: debounced query
 * (as in highcall's useUserSearch), and `highlightSearchTerms` from `config/search/search-utils`
 * — the same helper ManageUsersPage and ManageOrganizationsPage already use — so matched
 * substrings are emphasised the same way everywhere.
 *
 * Search runs SERVER-side (`/api/workflows/admin/users/search`). Filtering a client-side page of
 * 50 rows would silently fail to find user 51, which is the failure mode this component exists to
 * remove.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { highlightSearchTerms } from '@/config/search/search-utils';
import { Loader2, X, Check } from 'lucide-react';

export interface AdminUserResult {
  id: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  trigUserEmail: string | null;
  company: string | null;
  trigUserSubscriptionName: string | null;
  isInactive: boolean | null;
}

interface UserSearchComboboxProps {
  /** Fired when a user is picked (or cleared, with null). */
  onSelect: (user: AdminUserResult | null) => void;
  selected?: AdminUserResult | null;
  placeholder?: string;
  /** Render as a multi-select that keeps picking; the parent owns the accumulated list. */
  multi?: boolean;
  label?: string;
  id?: string;
}

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

export function displayName(user: AdminUserResult): string {
  const joined = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return user.fullName || joined || user.trigUserEmail || user.id;
}

export function UserSearchCombobox({
  onSelect,
  selected,
  placeholder = 'Search by name or email…',
  multi = false,
  label,
  id
}: UserSearchComboboxProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce so a five-letter name is one request, not five.
  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'user-search', debounced],
    queryFn: () => api.get<{ success: boolean; data: AdminUserResult[] }>(
      `/api/workflows/admin/users/search?q=${encodeURIComponent(debounced)}`
    ),
    enabled: debounced.length >= MIN_QUERY,
    staleTime: 30_000
  });

  const results = useMemo(() => data?.data ?? [], [data]);

  useEffect(() => setActiveIndex(0), [results]);

  // Close on outside click, so the list does not sit over the rest of the form.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (user: AdminUserResult) => {
    onSelect(user);
    setOpen(false);
    // Multi-select clears so the next name can be typed; single-select keeps the chosen name
    // visible, because an input that empties itself after a pick looks like it failed.
    setQuery(multi ? '' : displayName(user));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i - 1 + results.length) % results.length); }
    if (e.key === 'Enter') { e.preventDefault(); choose(results[activeIndex]); }
    if (e.key === 'Escape') setOpen(false);
  };

  const showEmpty = open && debounced.length >= MIN_QUERY && !isFetching && results.length === 0;

  return (
    <div className="space-y-1" ref={containerRef}>
      {label && <label htmlFor={id} className="text-sm font-medium">{label}</label>}

      {!multi && selected && (
        <div className="flex items-center justify-between rounded-md border bg-gray-50 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{displayName(selected)}</p>
            <p className="truncate text-xs text-gray-500">{selected.trigUserEmail || 'no email on file'}</p>
          </div>
          <button
            type="button"
            className="ml-2 text-gray-400 hover:text-gray-700"
            aria-label="Clear selected user"
            onClick={() => { onSelect(null); setQuery(''); }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {(multi || !selected) && (
        <div className="relative">
          <Input
            id={id}
            value={query}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
          {isFetching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
          )}

          {open && (results.length > 0 || showEmpty) && (
            <div className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-white shadow-lg">
              {showEmpty && (
                <p className="px-3 py-3 text-sm text-gray-500">No user matches “{debounced}”.</p>
              )}
              {results.map((user, i) => (
                <button
                  type="button"
                  key={user.id}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(user)}
                  className={`flex w-full items-start justify-between gap-2 px-3 py-2 text-left ${
                    i === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">
                      {highlightSearchTerms(displayName(user), debounced)}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {highlightSearchTerms(user.trigUserEmail || 'no email on file', debounced)}
                      {user.company ? <> · {highlightSearchTerms(user.company, debounced)}</> : null}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 pt-0.5">
                    {user.isInactive && (
                      <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] uppercase text-gray-600">
                        inactive
                      </span>
                    )}
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600">
                      {user.trigUserSubscriptionName || 'starter'}
                    </span>
                    {selected?.id === user.id && <Check className="h-3.5 w-3.5 text-green-600" />}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {query.trim().length > 0 && query.trim().length < MIN_QUERY && (
        <p className="text-xs text-gray-400">Keep typing — at least {MIN_QUERY} characters.</p>
      )}
    </div>
  );
}

export default UserSearchCombobox;
